import { Badge, DataTable, InlineStack, Link, Typography, type DataTableColumn } from '@pv-frontend/ui-components-web';
import {
    statusBadge,
    partialCoverageWarning,
    triggerLabel,
    scopeLabel,
    rollupSummary,
    formatTimestamp,
} from './crawl-format';

/** One crawl run as the list renders it — the stored summary, no per-agent children. */
export interface CrawlRunRow {
    id: string;
    trigger: string;
    requestedByOperator: string;
    scope: string;
    status: string;
    requestedUtc: string;
    startedUtc: string | null;
    completedUtc: string | null;
    siteId: string | null;
    sitesCrawled: number;
    /** How many sites reported success on only part of their book — the under-capture signal (pv-mono#266). */
    sitesPartiallyCrawled: number;
    listingsAdded: number;
    listingsUpdated: number;
    listingsUnchanged: number;
    listingsMissed: number;
    listingsDelisted: number;
    rejectsQueued: number;
}

const columns: ReadonlyArray<DataTableColumn<CrawlRunRow>> = [
    {
        key: 'requested',
        header: 'Requested',
        render: (run) => (
            <Typography variant="body-sm" numberOfLines={1}>
                {formatTimestamp(run.requestedUtc)}
            </Typography>
        ),
    },
    {
        key: 'trigger',
        header: 'Trigger',
        render: (run) => (
            <Badge tone={run.trigger === 'NIGHTLY' ? 'neutral' : 'primary'}>{triggerLabel(run.trigger)}</Badge>
        ),
    },
    {
        key: 'operator',
        header: 'Operator',
        render: (run) => (
            <Typography variant="body-sm" tone="text-secondary" numberOfLines={1}>
                {run.requestedByOperator}
            </Typography>
        ),
    },
    {
        key: 'scope',
        header: 'Scope',
        render: (run) => (
            <Typography variant="body-sm" tone="text-secondary">
                {scopeLabel(run.scope)}
            </Typography>
        ),
    },
    {
        key: 'status',
        header: 'Status',
        render: (run) => {
            const look = statusBadge(run.status);
            const partial = partialCoverageWarning(run.sitesPartiallyCrawled);
            return (
                <InlineStack gap="150" blockAlign="center" wrap>
                    <Badge tone={look.tone} dot testID={`status-${run.id}`}>
                        {look.label}
                    </Badge>
                    {/* Under-capture doesn't fail a run, so "Completed" alone would hide it — this is what
                        turns spotting it by eye into a glance at the history (pv-mono#266). */}
                    {partial ? (
                        <Badge tone="warning" testID={`partial-${run.id}`}>
                            {partial}
                        </Badge>
                    ) : null}
                </InlineStack>
            );
        },
    },
    {
        key: 'rollups',
        header: 'Added · updated · missed',
        align: 'end',
        render: (run) => (
            <Typography variant="body-sm" tone="text-secondary">
                {rollupSummary(run)}
            </Typography>
        ),
    },
    {
        key: 'view',
        header: '',
        align: 'end',
        render: (run) => <Link href={`/listings/crawls/${run.id}`}>View</Link>,
    },
];

/**
 * The crawl-run history table (pv-frontend#163) — newest first, one row per run.
 * Presentational only: the container feeds it rows and re-reads them on a poll
 * while any run is active. Each row links into the per-agent drill-in.
 */
export function CrawlRunsList({ runs }: { runs: ReadonlyArray<CrawlRunRow> }) {
    return (
        <DataTable
            columns={columns}
            rows={runs}
            keyExtractor={(run) => run.id}
            testID="crawl-runs"
            emptyState={
                <InlineStack gap="200">
                    <Typography tone="text-secondary">
                        No crawl runs yet. Use “Run crawl” to trigger one.
                    </Typography>
                </InlineStack>
            }
        />
    );
}
